<template>
  <div v-if="store.data" class="itinerary-page" :data-projection-preview-visible="projectionPreview || undefined">
    <div v-if="projectionPreview" class="projection-preview-context" data-projection-preview-region="context">
      <strong>Unlisted evaluation preview</strong>
      <span>Hero and media are intentionally unavailable</span>
    </div>
    <div class="itinerary-page-tags" v-if="store.data.draftOrEditing && !projectionPreview">
      <div class="itinerary-tag">Draft</div>
    </div>
    <template v-if="isPreview && !projectionPreview">
      <BackButton
        :handleClick="goBackToReviewKeyDetails"
        text="Back to review key details"
        :href="store.data?.draftItineraryUrl"
      />
    </template>
    <ItineraryTitle
      :editable="isPreview && !projectionPreview"
      :data-projection-preview-region="projectionPreview ? 'title' : undefined"
    />
    <ItineraryGallery v-if="!projectionPreview" :editable="isPreview" />
    <ItineraryMobileNavBar v-if="!projectionPreview" />

    <div v-if="desktop || projectionPreview" class="itinerary-body">
      <div class="main">
        <ItinerarySummary class="boxed" :data-projection-preview-region="projectionPreview ? 'summary' : undefined" />
        <ItineraryTourhero v-if="!projectionPreview" class="boxed" :editable="isPreview" />
        <ItineraryDetails
          class="boxed"
          :editable="isPreview && !projectionPreview"
          :data-projection-preview-region="projectionPreview ? 'itinerary' : undefined"
        />
      </div>

      <div class="aside">
        <div>
          <div :data-projection-preview-region="projectionPreview ? 'promotion' : undefined">
            <EarlyBirdCouponBanner />
          </div>
          <ItineraryPrice class="boxed" :data-projection-preview-region="projectionPreview ? 'pricing' : undefined" />
        </div>
        <ItineraryInterest v-if="!projectionPreview" />
        <ItineraryTopCustomerReviews v-if="!projectionPreview && store.displayReviews" class="boxed" />
        <ItineraryFellowExplorers v-if="!projectionPreview" class="boxed" />
        <ItineraryLocalGuide v-if="!projectionPreview" class="boxed" />
      </div>
    </div>

    <div v-if="mobile && !projectionPreview" class="itinerary-body">
      <div>
        <EarlyBirdCouponBanner />
        <ItineraryPrice />
        <ItineraryTopCustomerReviews v-if="!projectionPreview && store.displayReviews" class="m-mt" />
      </div>
      <ItinerarySummary />
      <ItineraryTourhero v-if="!projectionPreview" :editable="isPreview" />
      <ItineraryInterest v-if="!projectionPreview" />
      <ItineraryFellowExplorers v-if="!projectionPreview" />
      <ItineraryDetails class="m-mt" :editable="isPreview && !projectionPreview" />
    </div>

    <ProjectionPreviewPackages v-if="projectionPreview" class="boxed m-mt" />
    <ItineraryAccommodation
      class="boxed m-mt"
      :data-projection-preview-region="projectionPreview ? 'accommodation' : undefined"
    />
    <ItineraryLocalGuide v-if="!projectionPreview && mobile" class="m-mt" />

    <div class="itinerary-outline">
      <ItineraryIncludes
        class="boxed m-mt"
        :data-projection-preview-region="projectionPreview ? 'included' : undefined"
      />
      <ItineraryExcludes
        class="boxed m-mt"
        :data-projection-preview-region="projectionPreview ? 'excluded' : undefined"
      />
    </div>

    <ItineraryFaq v-if="!projectionPreview" class="boxed m-mt" />
    <ItineraryCancellationPolicy v-if="!projectionPreview" class="boxed m-mt" />
    <ItineraryAddons v-if="!projectionPreview" class="boxed m-mt" />
    <ItineraryCustomerReviews v-if="!projectionPreview && store.displayReviews" class="boxed m-mt" header />
    <CustomerReviewsModal v-if="!projectionPreview && store.displayReviews" />

    <ItineraryMediaCredits v-if="!projectionPreview" class="m-mt" />
  </div>

  <ItineraryFeedback v-if="isPreview && !projectionPreview" />

  <Teleport v-if="!projectionPreview" to="body">
    <StickyBottomBar v-if="isPreview">
      <ItineraryPreviewBanner />
    </StickyBottomBar>
    <transition v-else name="slide-y">
      <ItineraryStickyCTA v-if="!store.pricePanelIsVisible" />
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useResponsive } from '@/helpers/vueHelpers.js'
import { useItineraryStore } from '@/stores/itinerary.js'
import ItineraryTitle from '@/modules/Itinerary/ItineraryTitle.vue'
import ItineraryGallery from '@/modules/Itinerary/ItineraryGallery.vue'
import ItineraryMobileNavBar from '@/modules/Itinerary/ItineraryMobileNavBar.vue'
import ItinerarySummary from '@/modules/Itinerary/ItinerarySummary.vue'
import ItineraryTourhero from '@/modules/Itinerary/ItineraryTourhero.vue'
import ItineraryDetails from '@/modules/Itinerary/ItineraryDetails.vue'
import ItineraryPrice from '@/modules/Itinerary/ItineraryPrice.vue'
import ItineraryInterest from '@/modules/Itinerary/ItineraryInterest.vue'
import ItineraryFellowExplorers from '@/modules/Itinerary/ItineraryFellowExplorers.vue'
import ItineraryLocalGuide from '@/modules/Itinerary/ItineraryLocalGuide.vue'
import ItineraryAccommodation from '@/modules/Itinerary/ItineraryAccommodation.vue'
import ItineraryIncludes from '@/modules/Itinerary/ItineraryIncludes.vue'
import ItineraryExcludes from '@/modules/Itinerary/ItineraryExcludes.vue'
import ItineraryFaq from '@/modules/Itinerary/ItineraryFaq.vue'
import ItineraryCancellationPolicy from '@/modules/Itinerary/ItineraryCancellationPolicy.vue'
import ItineraryAddons from '@/modules/Itinerary/ItineraryAddons.vue'
import ItineraryMediaCredits from '@/modules/Itinerary/ItineraryMediaCredits.vue'
import ItineraryStickyCTA from '@/modules/Itinerary/ItineraryStickyCTA.vue'
import EarlyBirdCouponBanner from '@/modules/Itinerary/Components/EarlyBirdCouponBanner.vue'
import ItineraryCustomerReviews from '@/modules/Itinerary/ItineraryCustomerReviews.vue'
import CustomerReviewsModal from '@/modules/Itinerary/Components/CustomerReviewsModal.vue'
import ItineraryTopCustomerReviews from '@/modules/Itinerary/ItineraryTopCustomerReviews.vue'
import StickyBottomBar from '@/components/Theme2/StickyBottomBar.vue'
import ItineraryPreviewBanner from '@/modules/Itinerary/ItineraryPreviewBanner.vue'
import BackButton from '@/modules/Itinerary/Components/BackButton.vue'
import { redirectTo } from '@/utils/functions'
import { useAssociateProductToChatroom } from '@/helpers/hooks'
import ItineraryFeedback from '@/modules/Itinerary/Components/ItineraryFeedback/index.vue'
import ProjectionPreviewPackages from '@/modules/Itinerary/ProjectionPreviewPackages.vue'
import type { ProjectionPreviewInput } from '@/modules/Itinerary/projectionPreviewInput'
import { logErrorOnSentry } from '@/utils/errorReportingUtils.js'
import { computed } from 'vue'
import { useItinerarySectionLink } from './useItinerarySectionLink'

const { mobile, desktop } = useResponsive()
const store = useItineraryStore()
useItinerarySectionLink(computed(() => Boolean(store.data)))

const props = withDefaults(
  defineProps<{
    isPreview?: boolean
    projectionPreviewInput?: ProjectionPreviewInput | null
  }>(),
  { isPreview: false, projectionPreviewInput: null },
)

const projectionPreview = computed(() => props.projectionPreviewInput !== null)

if (props.projectionPreviewInput) {
  void store.initializeProjectionPreview(props.projectionPreviewInput).catch(error => {
    logErrorOnSentry(error, { component: 'Itinerary', function: 'initializeProjectionPreview' })
  })
} else {
  void store.initialize().catch(error => {
    logErrorOnSentry(error, { component: 'Itinerary', function: 'initialize' })
  })
}

if (props.isPreview && !projectionPreview.value) {
  useAssociateProductToChatroom()
}

const goBackToReviewKeyDetails = () => {
  redirectTo(store.data?.draftItineraryUrl)
}
</script>

<style lang="scss" scoped>
$gap: 24px;

@include slide-y-transition(100%, 100%, 250ms);

.itinerary-page {
  @include base-font;
  @include flex-column;
  padding-top: 32px;
  gap: $gap;
  overflow-wrap: anywhere;
}

.projection-preview-context {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: $spacing-2;
  border: 1px solid $grey-50;
  border-radius: $spacing-2;
  padding: $spacing-3;
  color: $grey-100;
}

.itinerary-body {
  display: grid;
  gap: $gap;

  @include desktop {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .main,
  .aside {
    @include flex-column($gap);
  }

  @include desktop('.main') {
    grid-column: span 2;
  }
}

.itinerary-outline {
  display: grid;
  gap: $gap;

  @include desktop {
    grid-template-columns: 1fr 1fr;
  }
}

@include desktop('.boxed') {
  border-radius: 15px;
  border: 1px solid $grey-50;
  padding: 32px;
}

@include mobile('.m-mt') {
  margin-top: $gap;
}

.itinerary-page-tags {
  display: flex;
  gap: $spacing-3;
}

.itinerary-tag {
  color: $brand-primary;
  text-transform: uppercase;
  border-radius: $spacing-1;
  border: 1px solid $brand-primary;
  padding: 6px $spacing-3;
  font-size: $text-xs;
  font-weight: $font-bold;
  display: inline-block;

  @media (min-width: $breakpoint-m) {
    font-size: $text-sm;
  }
}

:deep(a) {
  color: $brand-tertiary;
  font-weight: unset;
}
</style>
